import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Col, Container, ProgressBar, Row } from "react-bootstrap";
import { internApi, matchApi } from "../../api";
import { EventResolvingVideo } from "./EventResolvingVideo";
import { toast } from "react-toastify";
import Preloader from "@/components/Elements/Preloader";
import EventButton from "@/components/Events/EventButton";
import { SelectedEvents } from "./SelectedEvents/SelectedEvents";
import { StyledContainer } from "./SelectedEvents/StyledComponents";
import Switch from "@/components/Elements/switch";

const EventsResolving = () => {
  const { videoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);

  const [task, setTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [selectedActiveCandidates, setSelectedActiveCandidates] = useState([]);
  const [selectedPassiveCandidates, setSelectedPassiveCandidates] = useState(
    []
  );
  const [allSelectedBoxes, setAllSelectedBoxes] = useState([]);
  const [startEventTime, setStartEventTime] = useState(0);
  const [endEventTime, setEndEventTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");

  const [isStudy, setIsStudy] = useState(false);
  const [match, setMatch] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [taskProgress, setTaskProgress] = useState({
    blockedCount: 0,
    fullCount: 0,
  });
  const [activeCandidates, setActiveCandidates] = useState([]);
  const [passiveCandidates, setPassiveCandidates] = useState([]);
  const [previousTaskId, setPreviousTaskId] = useState(null);
  const [taskType, setTaskType] = useState("event");
  const [inRange, setInRange] = useState(false); // possibility to labeling
  const [enabled, setEnabled] = useState(false);
  const [showBoxes, setShowBoxes] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [messages, setMessages] = useState([]);

  const progress = `${taskProgress.blockedCount}/${taskProgress.fullCount}`;

  useEffect(() => {
    const pathname = location.pathname;
    console.log("is study", pathname.includes("/intern"));
    if (pathname.includes("/intern")) {
      setIsStudy(true);
    } else {
      setIsStudy(false);
    }
  }, [location]);

  useEffect(() => {
    if (isStudy) {
      fetchMatchTask();
      return;
    }

    if (videoId === undefined || videoId === null) return;

    const fetchData = async () => {
      const response = await matchApi.getMatch(videoId);

      setMatch(response);
      setVideoUrl(response?.video.origin);
      await fetchMatchTask();
    };

    fetchData();
  }, [videoId, isStudy]);

  useEffect(() => {
    const pathname = location.pathname;
    setTaskType(pathname.includes("events-resolving") ? "event" : "ball_touch");
  }, [location]);

  useEffect(() => {
    if (!task) return;

    setStartEventTime(task.frame_start * (1 / task.fps));
    setEndEventTime(task.frame_end * (1 / task.fps));
  }, [task, taskType]);

  useEffect(() => {
    if (!task) return;
    if (!showAll) {
      setActiveCandidates(task.candidates_active);
      setPassiveCandidates(task.candidates_passive);
    } else {
      setActiveCandidates(
        task.candidates_active.length
          ? [...task.candidates_active, ...task.additional_candidates]
          : []
      );
      setPassiveCandidates(
        task.candidates_passive.length
          ? [...task.candidates_passive, ...task.additional_candidates]
          : []
      );
    }
  }, [showAll, task]);

  useEffect(() => {
    if (!task || !task.frame_start || !task.frame_end || !task.fps) return;

    const keyDownHandler = async (e) => {
      const { code, shiftKey, ctrlKey, metaKey } = e;
      if (code === "Space") {
        e.preventDefault();
      }
      if (code === "Enter" && !isLoading) {
        submitTaskResolve();
      }

      if (!enabled) return;

      if (code === "KeyS") {
        handleStartEventTime();
      } else if (code === "KeyG") {
        handleEndEventTime();
      }

      task.buttons.forEach((label) => {
        const key = "Key" + label.split("(")[1].charAt(0).toUpperCase();
        if (code === key) {
          onSelectedEvent(label);
        }
      });

      if (code.includes("Digit")) {
        const candidateId = code.split("Digit")[1];
        // Call your specific function here with the number key
        if (shiftKey) {
          if (!task.candidates_passive?.length) return;
          onPassiveCandidateClick(Number(candidateId));
        } else {
          if (!task.candidates_active?.length) return;
          onActiveCandidateClick(Number(candidateId));
        }
      }

      if (isStudy) return;

      if (code === "KeyZ" && (ctrlKey || metaKey)) {
        e.preventDefault();
        if (previousTaskId) await fetchMatchTask(false, previousTaskId);
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [
    task,
    selectedActiveCandidates,
    selectedPassiveCandidates,
    startEventTime,
    endEventTime,
    selectedEvent,
    isLoading,
    enabled,
    previousTaskId,
    isStudy,
  ]);

  const clearCandidates = () => {
    setSelectedPassiveCandidates([]);
    setSelectedActiveCandidates([]);
    setAllSelectedBoxes([]);
    if (task) {
      setStartEventTime(task.frame_start * (1 / task.fps));
      setEndEventTime(task.frame_end * (1 / task.fps));
    } else {
      setStartEventTime(0);
      setEndEventTime(0);
    }
  };

  useEffect(() => {
    if (submitted || !inRange) {
      setEnabled(false);
    } else {
      setEnabled(true);
    }
  }, [submitted, inRange]);

  const fetchMatchTask = useCallback(
    async (isNewFetchNewTask = false, previousTaskId = null) => {
      setMessages([]);
      if (isStudy) {
        try {
          const res = await internApi.getMatchTask(taskType);
          const { id, match_number, task } = res;
          const { url } = await matchApi.getFilePath({
            matchId: match_number,
            fileType: "study_video",
          });
          setTaskId(id);
          setTask(task);
          setVideoUrl(url);
        } catch (err) {
          console.log("err", err);
          toast.error("Something went wrong.");
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (!location) return;
      setIsLoading(true);
      let response = null;
      const query = new URLSearchParams(location.search);
      const targetTaskId = previousTaskId || query.get("taskId");

      const eventPath = "events-resolving";
      const pathname = location.pathname.split("/")[2];
      const isEvent = pathname === eventPath ? "event" : "ball_touch";

      try {
        if (targetTaskId && !isNewFetchNewTask) {
          response = await matchApi.getMatchTask(videoId, targetTaskId);
        } else {
          response = await matchApi.postMatchTask(videoId, isEvent);
        }

        const { task, id, blocked_count, full_count } = response;
        navigate(`/panel/${pathname}/${videoId}?taskId=${id}`);
        if (task) {
          setTask(task);
          setTaskId(id);
          setTaskProgress({
            blockedCount: blocked_count,
            fullCount: full_count,
          });
        } else {
          toast("There are no tasks for this match");
          navigate("/panel");
          // await postVideoContinue();
        }
      } catch (error) {
        toast(error.message);
        if (error.code === 409) {
          await fetchMatchTask(true);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [location, taskType, isStudy]
  );

  const fetchStudyTask = async () => {
    setSubmitted(false);
    await fetchMatchTask(true);
  };

  const submitTaskResolve = async () => {
    if (selectedEvent.length === 0) {
      toast.warning("Please choose at least one event.");
      return;
    }

    setIsLoading(true);

    try {
      if (isStudy) {
        const data = await internApi.postMatchTask(
          taskId,
          selectedEvent,
          taskType
        );
        if (data.error_messages?.message) {
          setMessages(data.error_messages?.message);
        }
        setSubmitted(true);
      } else {
        setPreviousTaskId(taskId);
        await matchApi.postMatchTaskResult({
          matchId: videoId,
          taskId,
          taskResult: selectedEvent,
          taskType,
        });
        await fetchMatchTask(true);
      }
      toast.success("Result sent");

      setSelectedEvent([]);
      clearCandidates();
    } catch (error) {
      if (error.code === 409) {
        await fetchMatchTask(true);
      } else {
        toast(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onActiveCandidateClick = (id) => {
    if (!activeCandidates.includes(id)) return;

    if (selectedActiveCandidates.includes(id)) {
      setSelectedActiveCandidates([]);
      return;
    }
    setSelectedActiveCandidates([id]);
  };

  const onPassiveCandidateClick = (id) => {
    if (!passiveCandidates.includes(id)) return;

    if (selectedPassiveCandidates.includes(id)) {
      setSelectedPassiveCandidates([]);
      return;
    }
    setSelectedPassiveCandidates([id]);
  };

  const onSelectedEvent = (event) => {
    if (!task || startEventTime === null || endEventTime === null) return;

    const { question, frame_start, frame_end, extra } = task;
    const newEvent = {
      answer: [event],
      question,
      frame_start,
      frame_end,
      candidates_active: selectedActiveCandidates,
      candidates_passive: selectedPassiveCandidates,
      event_time: videoRef.current.currentTime,
      start_event_time: startEventTime,
      end_event_time: endEventTime,
      extra: extra,
    };

    setSelectedEvent([...selectedEvent, newEvent]);
    clearCandidates();
  };

  const onDeleteSelectedEvent = (event) => {
    const filteredEvent = selectedEvent.filter((item) => item.answer !== event);
    setSelectedEvent(filteredEvent);
  };

  const onDeleteSelectedCandidates = (eventTime, type, candidate) => {
    const filteredEvent = selectedEvent.map((event) => {
      if (event.event_time !== eventTime) return event;

      event[type] = event[type].filter((item) => item !== candidate);
      return event;
    });
    setSelectedEvent(filteredEvent);
  };

  const handleStartEventTime = () => {
    const currentTime = videoRef.current.currentTime;
    setStartEventTime(currentTime);
  };

  const handleEndEventTime = () => {
    const currentTime = videoRef.current.currentTime;
    setEndEventTime(currentTime);
  };

  useEffect(() => {
    setAllSelectedBoxes([
      ...selectedPassiveCandidates,
      ...selectedActiveCandidates,
    ]);
  }, [selectedActiveCandidates, selectedPassiveCandidates]);

  const onMouseDownHandler = (e) => {
    const { trackId } = e.target.attrs;
    if (!trackId || trackId === "ball") return;

    if (e.evt.button === 0) onActiveCandidateClick(Number(trackId));
    else if (e.evt.button === 2) onPassiveCandidateClick(Number(trackId));
  };

  return (
    <>
      {videoUrl && task ? (
        <div className="tw-px-2 tw-py-2 sm:tw-px-8 sm:tw-py-3 tw-flex tw-justify-center w-full">
          <div className="w-full xl:tw-w-[1280px]">
            {!isStudy && taskProgress.fullCount && (
              <StyledContainer>
                <div>
                  Markup progress {taskProgress.blockedCount}/
                  {taskProgress.fullCount}
                </div>
                <ProgressBar
                  min={0}
                  max={taskProgress.fullCount}
                  now={taskProgress.blockedCount}
                  label={progress}
                />
              </StyledContainer>
            )}

            <div className="tw-flex tw-gap-2 tw-mb-2">
              <Switch
                label="Show boxes"
                checked={showBoxes}
                onChange={setShowBoxes}
              />
              {taskType === "ball_touch" && (
                <Switch
                  label="Show all"
                  checked={showAll}
                  onChange={setShowAll}
                />
              )}
              <Switch
                label="Expand"
                checked={expanded}
                onChange={setExpanded}
              />
            </div>

            <EventResolvingVideo
              videoRef={videoRef}
              src={videoUrl}
              task={task}
              matchId={videoId}
              taskType={taskType}
              selectedBoxes={allSelectedBoxes}
              showBoxes={showBoxes}
              showAll={showAll}
              expanded={expanded}
              setEnabled={setInRange}
              onMouseDownHandler={onMouseDownHandler}
            />

            <Row className="justify-content-center mt-3">
              <Col xs={"auto"} className="d-flex gap-1">
                <EventButton
                  key={"Start event time"}
                  text={"Start event time (S)"}
                  onChangeEvent={handleStartEventTime}
                  disabled={!enabled}
                />
                <EventButton
                  key={"End event time"}
                  text={"End event time (G)"}
                  onChangeEvent={handleEndEventTime}
                  disabled={!enabled}
                />
              </Col>

              <Col xs={"auto"} className="d-flex gap-1">
                <div>
                  start time:
                  {startEventTime.toFixed(2).replace(/[.,]00$/, "")}
                </div>
                <div>
                  end time:
                  {endEventTime.toFixed(2).replace(/[.,]00$/, "")}
                </div>
              </Col>
            </Row>

            {activeCandidates.length ? (
              <Row className="justify-content-center mt-3">
                <Col xs={"auto"}>
                  <span>Active candidates</span>
                </Col>
                <Col
                  xs={"auto"}
                  className="d-flex justify-content-center gap-1"
                >
                  {activeCandidates.map((id) => (
                    <EventButton
                      key={id}
                      text={id}
                      onChangeEvent={() => onActiveCandidateClick(id)}
                      color={
                        selectedActiveCandidates?.includes(id)
                          ? "success"
                          : "primary"
                      }
                      disabled={!enabled}
                    />
                  ))}
                </Col>
              </Row>
            ) : null}
            {passiveCandidates.length ? (
              <Row className="justify-content-center mt-3">
                <Col xs={"auto"}>
                  <span>Passive candidates</span>
                </Col>
                <Col
                  xs={"auto"}
                  className="d-flex justify-content-center gap-1"
                >
                  {passiveCandidates.map((id) => (
                    <EventButton
                      key={id}
                      text={id}
                      onChangeEvent={() => onPassiveCandidateClick(id)}
                      color={
                        selectedPassiveCandidates?.includes(id)
                          ? "success"
                          : "primary"
                      }
                      disabled={!enabled}
                    />
                  ))}
                </Col>
              </Row>
            ) : null}
            <Row className="justify-content-center mt-3">
              <Col xs={"auto"}>
                <span>{task.question}</span>
              </Col>
              <Col
                xs={"auto"}
                className="d-flex justify-content-center flex-wrap gap-1"
              >
                {task.buttons.map((currentEvent) => (
                  <EventButton
                    key={currentEvent}
                    text={currentEvent}
                    onChangeEvent={() => onSelectedEvent(currentEvent)}
                    color="primary"
                    disabled={!enabled}
                  />
                ))}
              </Col>
            </Row>
            {selectedEvent[0] && (
              <SelectedEvents
                list={selectedEvent}
                onDelete={onDeleteSelectedEvent}
                onDeleteCandidate={onDeleteSelectedCandidates}
              />
            )}
            <Row className="justify-content-center mt-3">
              <Col xs={"auto"} className="d-flex justify-content-center gap-2">
                {isStudy ? (
                  <>
                    {!submitted ? (
                      <EventButton
                        className="tw-px-3 tw-py-3 tw-text-lg tw-font-semibold"
                        onClick={() => submitTaskResolve()}
                        disabled={isLoading}
                      >
                        Submit (Enter)
                      </EventButton>
                    ) : (
                      <EventButton
                        className="tw-px-3 tw-py-3 tw-text-lg tw-font-semibold"
                        onClick={() => fetchStudyTask()}
                        disabled={isLoading}
                      >
                        Move to next task (Enter)
                      </EventButton>
                    )}
                  </>
                ) : (
                  <EventButton
                    className="tw-px-3 tw-py-3 tw-text-lg tw-font-semibold"
                    onClick={() => submitTaskResolve()}
                    disabled={isLoading}
                  >
                    Move to next task (Enter)
                  </EventButton>
                )}
              </Col>
            </Row>
            <Row className="justify-content-center mt-3">
              <Col xs={12} className="text-center">
                {messages.map((message, index) => (
                  <p key={index}>{message}</p>
                ))}
                {submitted && messages.length === 0 && <p>Correct answer submitted</p>}
              </Col>
            </Row>
          </div>
        </div>
      ) : (
        <Preloader className="m-auto" />
      )}
    </>
  );
};

export default EventsResolving;
